var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Euler, Matrix4, Quaternion, Vector2 } from '@skand/math';
import { Cartographic, Ellipsoid } from '@skand/math';
import proj4 from 'proj4';
import { spatialReferenceSystems, } from '../DataTypes';
import Papa from 'papaparse';
export class PegasusDirectionalLoader {
    constructor() {
        this.missingData = [];
    }
    /**
     * Convert a position in some coordinate system to ECEF
     *
     * @param x          x-position in source coordinate system
     * @param y          y-position in source coordinate system
     * @param z          z-position in source coordinate system
     * @param projection Projection string of source coordinate system
     */
    calculatePosition(x, y, z, projection) {
        try {
            const ecefSpace = proj4(projection, 'EPSG:4326').forward({ x, y, z });
            return Ellipsoid.WGS_84.cartesianForCartographic(new Cartographic((ecefSpace.x * Math.PI) / 180, (ecefSpace.y * Math.PI) / 180, ecefSpace.z));
        }
        catch (error) {
            throw new Error(`Invalid Projection Error: ${error}`);
        }
    }
    /**
     * Calculate the rotation of the panorama in ECEF
     *
     * @param x          x-position in source coordinate system
     * @param y          y-position in source coordinate system
     * @param z          z-position in source coordinate system
     * @param omega      Omega angle
     * @param phi        Phi angle
     * @param kappa      Kappa angle
     * @param projection Projection string of source coordinate system
     */
    calculateRotation(x, y, z, omega, phi, kappa, projection) {
        const eps = 0.001;
        const origin = this.calculatePosition(x, y, z, projection);
        const xOffset = this.calculatePosition(x + eps, y, z, projection);
        const yOffset = this.calculatePosition(x, y + eps, z, projection);
        // Calculate rotation axes
        const xAxis = xOffset.clone().sub(origin).normalize();
        const yAxis = yOffset.clone().sub(origin).normalize();
        const zAxis = xAxis.clone().cross(yAxis).normalize();
        xAxis.copy(yAxis).cross(zAxis).normalize();
        // Create final rotation matrix
        const basis = new Matrix4().makeBasis(xAxis, yAxis, zAxis);
        const localRotation = new Matrix4().makeRotationFromEuler(new Euler((omega * Math.PI) / 200, (phi * Math.PI) / 200, (kappa * Math.PI) / 200, 'XYZ'));
        basis.multiply(localRotation);
        return new Quaternion().setFromRotationMatrix(basis);
    }
    /**
     * Parse the intrinsic camera properties.
     *
     * @param intrinsics
     * @param epsg
     * @returns
     */
    parseIntrinsics(intrinsics, epsg) {
        const lines = intrinsics.trim().split('\n');
        const camera = {
            id: 0,
            imageSize: new Vector2(),
            principalPoint: new Vector2(),
            spatialReferenceProj: epsg,
            sensorSize: 0,
            skew: 0,
            focalLength: 0,
            distortion: {
                k1: 0,
                k2: 0,
                k3: 0,
                p1: 0,
                p2: 0,
            },
        };
        for (const line of lines) {
            const [k, v] = line.split('=');
            if (k === 'Dist_CV_WIDTH') {
                camera.imageSize.x = Number.parseInt(v);
            }
            else if (k === 'Dist_CV_HEIGHT') {
                camera.imageSize.y = Number.parseInt(v);
            }
            else if (k === 'Dist_CV_CX') {
                camera.principalPoint.x = Number.parseFloat(v);
            }
            else if (k === 'Dist_CV_CY') {
                camera.principalPoint.y = Number.parseFloat(v);
            }
            else if (k === 'SensorSizeMM') {
                camera.sensorSize = Math.max(...v.split(',').map(Number.parseFloat));
            }
            else if (k === 'FocalLenghtMM') {
                camera.focalLength = Number.parseFloat(v);
            }
            else if (k === 'Dist_CV_K1') {
                camera.distortion.k1 = Number.parseFloat(v);
            }
            else if (k === 'Dist_CV_K2') {
                camera.distortion.k2 = Number.parseFloat(v);
            }
            else if (k === 'Dist_CV_K3') {
                camera.distortion.k3 = Number.parseFloat(v);
            }
            else if (k === 'Dist_CV_P1') {
                camera.distortion.p1 = Number.parseFloat(v);
            }
            else if (k === 'Dist_CV_P2') {
                camera.distortion.p2 = Number.parseFloat(v);
            }
        }
        return camera;
    }
    /**
     * Get the list of photos (filenames) that have missing data
     */
    getPhotosMissingData() {
        return this.missingData;
    }
    /**
     * Get the list of panoramic objects from an MLS CSV input string
     *
     * @param csv        CSV Pose string
     * @param intrinsics Intrinsic camera arguments string
     * @param epsg       EPSG code
     */
    read(csv, intrinsics, epsg) {
        return __awaiter(this, void 0, void 0, function* () {
            const photoGroup = {
                cameras: new Map().set(0, this.parseIntrinsics(intrinsics, epsg)),
                photos: [],
            };
            const parsed = Papa.parse(csv, {
                header: false,
                delimitersToGuess: ['; ', ', ', ';', ','],
            });
            for (const entry of parsed.data) {
                const values = Object.values(entry);
                // Parse individual column values
                const fileName = values[0];
                const x1 = parseFloat(values[2]);
                const y1 = parseFloat(values[3]);
                const z1 = parseFloat(values[4]);
                const omega = parseFloat(values[5]);
                const phi = parseFloat(values[6]);
                const kappa = parseFloat(values[7]);
                // Skip invalid data
                if (!fileName)
                    continue;
                if (isNaN(x1) ||
                    isNaN(y1) ||
                    isNaN(z1) ||
                    isNaN(omega) ||
                    isNaN(phi) ||
                    isNaN(kappa)) {
                    this.missingData.push(fileName);
                    continue;
                }
                const projection = spatialReferenceSystems[epsg].proj4;
                const position = this.calculatePosition(x1, y1, z1, projection);
                const rotation = this.calculateRotation(x1, y1, z1, omega, phi, kappa, projection);
                photoGroup.photos.push({
                    cameraId: 0,
                    fileName: fileName.split('.').slice(0, -1).join('.'),
                    position,
                    rotation,
                });
            }
            return photoGroup;
        });
    }
}
